import { useEffect, useState } from "react";
import {
    CommentFormControls,
    FormGifViewer,
    FormImageViewer,
    FormVideoViewer,
    MentionInput,
    SVG,
    UserPopover,
    customToaster
} from "ui";

import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "services/redux/store";
import { closeForm } from "services/redux/commentForm/commentForm";
import CommentPost from "../feed/CommentPost";
import { CommentFormObject } from "utils/types/generalTypes";
import { avatar } from "utils/assets_exports/images";
import { allowScroll, stopScroll } from "services/redux/site/site";

const CommentFormModal = () => {
    const open = useSelector((state: RootState) => state.commentForm.open);
    const dispatch = useDispatch<AppDispatch>();

    const mode = useSelector((state: RootState) => state.site.mode);

    const initialReply: CommentFormObject = {
        id: 0,
        type: "reply",
        text: "",
        mediaType: null,
        isValid: false
    };

    const [reply, setReply] = useState<CommentFormObject>(initialReply);
    const toggleValid = () => {
        if (
            !reply.text.length &&
            !reply.images &&
            !reply.video &&
            !reply.gif &&
            reply.isValid
        ) {
            setReply({ ...reply, isValid: false });
            return;
        }

        // set post to be valid
        if (!reply.isValid) {
            setReply({ ...reply, isValid: true });
            return;
        }
    };

    const handleRemoveMedia = () => {
        setReply({
            ...reply,
            images: undefined,
            mediaType: null,
            video: undefined,
            gif: undefined
        });
    };

    useEffect(() => {
        toggleValid();
    }, [reply.text, reply.images, reply.video, reply.gif]);

    // ------------------------- validate text part
    const handleChangeText = (val: string) => {
        if (val.length > 250) {
            return;
        } else {
            setReply({
                ...reply,
                text: val
            });
        }
    };

    // ------------------------- validate image part
    const handleChangeImage = (images: FileList) => {
        // validate image
        if (!images[0]) {
            return;
        }
        if (images.length > 4) {
            customToaster("error", "You can select up to 4 images only", mode);
            return;
        }

        const imagesArr: File[] = [];
        let valid = true;
        // eslint-disable-next-line
        for (let image of Array.from(images)) {
            if (!image.name.match(/\.(jpg|jpeg|png)$/)) {
                valid = false;
                break;
            } else {
                imagesArr.push(image);
            }
        }
        if (!valid) {
            customToaster("error", "Please select valid imag", mode);
            return;
        }
        setReply({
            ...reply,
            images: imagesArr,
            mediaType: "img",
            video: undefined,
            gif: undefined
        });
    };

    const removeImg = (img: File) => {
        if (!reply.images) {
            return;
        }

        const imagesArr = reply.images.filter((i) => i != img);

        if (imagesArr.length) {
            setReply({
                ...reply,
                images: imagesArr,
                mediaType: "img",
                video: undefined,
                gif: undefined
            });
        } else {
            setReply({
                ...reply,
                images: undefined,
                mediaType: null,
                video: undefined,
                gif: undefined
            });
        }
        // toggleValid();
    };

    // ------------------------- validate video part
    const handleChangeVideo = (videos: FileList) => {
        if (!videos[0]) {
            return;
        }
        if (videos.length > 1) {
            customToaster("error", "You can select 1 video only", mode);
            return;
        }

        if (!videos[0].name.match(/\.(mp4|mkv|webm|3gp)$/)) {
            customToaster("error", "Please select valid Video", mode);
        } else {
            setReply({
                ...reply,
                video: videos[0],
                mediaType: "vid",
                images: undefined,
                gif: undefined
            });
        }
    };

    const { emoji, gif } = useSelector((state: RootState) => state.commentForm);
    // useEffect to listen gif
    useEffect(() => {
        setReply({ ...reply, mediaType: "gif", gif });
    }, [gif]);

    // useEffect to listen to emoji
    useEffect(() => {
        setReply({ ...reply, text: reply.text + emoji });
    }, [emoji]);

    const handleClose = () => {
        dispatch(closeForm());
        setReply(initialReply);
    };

    // handle page scroll on open modal
    useEffect(() => {
        open ? dispatch(stopScroll()) : dispatch(allowScroll());
    }, [open]);

    return (
        <div
            className={`reply-modal modal is-small has-light-bg ${open ? "is-active" : ""}`}
        >
            <div className="modal-background"></div>
            <div className="modal-content">
                <div className="card is-new-content">
                    <div className="card-heading d-flex flex-j-end flex-a-center">
                        {/* <h1>Post Comment</h1> */}
                        <div className="close-wrap d-flex flex-j-end mr-10">
                            <span className="close-modal" onClick={handleClose}>
                                <SVG svg="x" size={16} />
                            </span>
                        </div>
                    </div>

                    <div className="card-body">
                        <CommentPost />
                        {/* {{-- post form part --}} */}
                        <div
                            className={`post-form d-flex flex-j-start gap-10 flex-a-start p-relative`}
                        >
                            <div className="card is-post card-heading">
                                <div className="user-block">
                                    <div className="image p-relative">
                                        <img
                                            src={avatar}
                                            data-user-popover="1"
                                            alt=""
                                        />
                                        <UserPopover />
                                    </div>
                                </div>
                            </div>

                            <div className="post-form-viewer">
                                {/* <TextArea
                                    text={reply.text}
                                    handleChangeText={handleChangeText}
                                /> */}
                                <MentionInput
                                    val={reply.text}
                                    setVal={handleChangeText}
                                />
                                {/* media viewer part  */}
                                <div className="media-displayer pt-10 pb-10">
                                    {/* selected image viewer   */}
                                    {reply.images ? (
                                        <FormImageViewer
                                            images={reply.images}
                                            removeImg={removeImg}
                                            isReply={true}
                                        />
                                    ) : (
                                        ""
                                    )}

                                    {/* selected video viewer */}
                                    {reply.video ? (
                                        <FormVideoViewer
                                            video={reply.video}
                                            handleRemoveMedia={
                                                handleRemoveMedia
                                            }
                                        />
                                    ) : (
                                        ""
                                    )}

                                    {/* gif viewer  */}
                                    {reply.gif ? (
                                        <FormGifViewer
                                            gif={reply.gif}
                                            handleRemoveMedia={
                                                handleRemoveMedia
                                            }
                                        />
                                    ) : (
                                        ""
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card-footer d-flex flex-j-between pt-10">
                        {/* media import btns  */}
                        <CommentFormControls
                            handleChangeImage={handleChangeImage}
                            handleChangeVideo={handleChangeVideo}
                        />

                        <button
                            type="button"
                            className="button is-solid is-primary primary-btn close-modal"
                        >
                            Reply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CommentFormModal;
